import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PALS } from "@/lib/pals-data";
import { GUIDES } from "@/lib/guides-data";
import { palsinoSyncIssues } from "@/lib/palsino-audit";


export const Route = createFileRoute("/link-audit")({
  head: () => ({
    meta: [
      { title: "Automated Link Audit — Pal Stars" },
      { name: "description", content: "Automated internal-link audit for every Pal, guide and top-level page on Pal Stars." },
      { property: "og:title", content: "Automated Link Audit — Pal Stars" },
      { property: "og:description", content: "Runs a live audit of internal links across the Pal Stars wiki." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/link-audit" },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/link-audit" }],
  }),
  component: LinkAuditPage,
});

type Row = { url: string; label: string; status: "pending" | "ok" | "fail"; code?: number };

const TOP_LEVEL = [
  "/", "/pals", "/breeding", "/mounts", "/capture-rates", "/map",
  "/elements", "/cards", "/guides", "/deck-builder", "/technology-tree", "/eidrolon",
  "/contact", "/favorites", "/privacy", "/terms", "/security-disclaimer", "/sitemap.xml",
];

function LinkAuditPage() {
  const [rows, setRows] = useState<Row[]>(() => {
    const urls: Row[] = [
      ...TOP_LEVEL.map((u) => ({ url: u, label: u, status: "pending" as const })),
      ...GUIDES.map((g) => ({ url: `/guides/${g.slug}`, label: `Guide · ${g.title}`, status: "pending" as const })),
      ...PALS.map((p) => ({ url: `/pals/${p.slug}`, label: `Pal · ${p.name}`, status: "pending" as const })),
    ];
    return urls;
  });
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setRunning(true);
      for (let i = 0; i < rows.length; i++) {
        if (cancelled) return;
        try {
          const res = await fetch(rows[i].url, { method: "HEAD" });
          setRows((cur) => cur.map((r, idx) => idx === i ? { ...r, status: res.ok ? "ok" : "fail", code: res.status } : r));
        } catch {
          setRows((cur) => cur.map((r, idx) => idx === i ? { ...r, status: "fail", code: 0 } : r));
        }
      }
      setRunning(false);
    }
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ok = rows.filter((r) => r.status === "ok").length;
  const fail = rows.filter((r) => r.status === "fail").length;
  const pending = rows.filter((r) => r.status === "pending").length;
  const palsinoIssues = palsinoSyncIssues();


  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Automated Link Audit</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Live HEAD-request audit of every top-level route, all guide slugs and a sample of Pal detail pages. Runs automatically on load.
      </p>
      <div className="mt-4 flex gap-3 text-xs">
        <span className="rounded-full bg-emerald-500/15 text-emerald-400 px-3 py-1">OK {ok}</span>
        <span className="rounded-full bg-rose-500/15 text-rose-400 px-3 py-1">Fail {fail}</span>
        <span className="rounded-full bg-muted px-3 py-1">Pending {pending}</span>
        {running && <span className="text-muted-foreground">Running…</span>}
      </div>

      <section className="mt-8">
        <h2 className="text-xl font-bold">Palsino mismatch regression check</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Compares the live slot-machine config against the guide pack, payout order and on-page copy.
        </p>
        <ul className="glass mt-3 divide-y rounded-xl text-sm">
          {palsinoIssues.map((i) => (
            <li key={i.check} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <span className={i.ok ? "text-emerald-400" : "text-rose-400"}>
                {i.ok ? "PASS" : "MISMATCH"} · {i.check}
              </span>
              <span className="text-xs text-muted-foreground">{i.detail}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="glass rounded-xl mt-6 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
              <th className="p-3">Route</th>
              <th className="p-3">Label</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.url} className="border-b hover:bg-card/60">
                <td className="p-3"><Link to={r.url as never} className="text-primary hover:underline">{r.url}</Link></td>
                <td className="p-3 text-muted-foreground">{r.label}</td>
                <td className="p-3">
                  <span className={
                    r.status === "ok" ? "text-emerald-400" :
                    r.status === "fail" ? "text-rose-400" : "text-muted-foreground"
                  }>
                    {r.status.toUpperCase()}{r.code ? ` · ${r.code}` : ""}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
